import { useCallback, useEffect, useRef, useState } from "react";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { ContextMenu } from "@/components/common/ContextMenu";
import { IconButton } from "@/components/common/IconButton";
import { KindFilterChip, Pill, SummaryChip } from "@/components/infra/InfraParts";
import {
	captionStyle,
	cellStyle,
	pageTitleStyle,
	sectionHeadingStyle,
} from "@/components/infra/infra-styles";
import {
	getInfraPlacement,
	restartEnvironment,
	stopEnvironment,
	type InfraEnvironment,
	type InfraPlacement,
} from "@/lib/api";
import { formatBytes } from "@/lib/format-bytes";
import { formatDateTime, formatTime } from "@/lib/format-datetime";
import {
	INFRA_NODE_KINDS,
	INFRA_NODE_KIND_LABELS,
	filterNodeGroupsByKind,
	filterPlacementByOrganization,
	formatAge,
	formatMilliCpu,
	groupEnvironmentsByNode,
	infraEnvironmentStatus,
	infraNodeAmiStatus,
	infraNodeKind,
	infraOrganizations,
	sortNodeGroupsByKind,
	toggleKind,
	usageRatio,
	type InfraNodeGroup,
	type InfraNodeKind,
} from "@/lib/infra-placement";
import { sum } from "@/lib/sum";
import { useToastStore } from "@/stores/useToastStore";

const REFRESH_INTERVAL_MS = 15_000;

type EnvironmentAction = "stop" | "restart";

const ENVIRONMENT_ACTION_LABELS: Record<EnvironmentAction, string> = {
	stop: "停止",
	restart: "再起動",
};

/**
 * 管理ページの「ノード」タブ (/infra、SuperAdmin のみ)。env ノードの増減と、全組織の環境がどのノードに
 * 載っているかを定期的に読み直して見せる。
 */
export function InfraPage() {
	const [placement, setPlacement] = useState<InfraPlacement | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [selectedKinds, setSelectedKinds] = useState<ReadonlySet<InfraNodeKind>>(new Set());
	const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | null>(null);
	const [pendingAction, setPendingAction] = useState<{
		action: EnvironmentAction;
		environment: InfraEnvironment;
	} | null>(null);
	const [actionNotice, setActionNotice] = useState<string | null>(null);
	const mounted = useRef(true);

	const load = useCallback(() => {
		setLoading(true);
		getInfraPlacement()
			.then((data) => {
				if (!mounted.current) return;
				setPlacement(data);
				setError(null);
			})
			.catch((err: Error) => {
				if (mounted.current) setError(err.message);
			})
			.finally(() => {
				if (mounted.current) setLoading(false);
			});
	}, []);

	useEffect(() => {
		mounted.current = true;
		load();
		const timer = window.setInterval(load, REFRESH_INTERVAL_MS);
		return () => {
			mounted.current = false;
			window.clearInterval(timer);
		};
	}, [load]);

	const confirmPendingAction = async () => {
		if (pendingAction === null) return;
		const { action, environment } = pendingAction;
		const result =
			action === "stop"
				? await stopEnvironment(environment.environmentId)
				: await restartEnvironment(environment.environmentId);
		setActionNotice(result.notice);
		setPendingAction(null);
		load();
	};
	const requestAction = (action: EnvironmentAction, environment: InfraEnvironment) => {
		setActionNotice(null);
		setPendingAction({ action, environment });
	};

	const now = placement === null ? new Date() : new Date(placement.observedAt);
	const organizations = placement === null ? [] : infraOrganizations(placement.environments);
	// 選んだ組織の環境が読み直しで無くなったら、全組織の表示に戻す。
	const organizationId = organizations.some((o) => o.id === selectedOrganizationId)
		? selectedOrganizationId
		: null;
	const { groups, unplaced } =
		placement === null
			? { groups: [], unplaced: [] }
			: filterPlacementByOrganization(
					groupEnvironmentsByNode(placement.nodes, placement.environments),
					organizationId,
				);
	const environmentCount = sum(groups, (group) => group.environments.length) + unplaced.length;
	const kindCounts = groups.reduce(
		(counts, group) => ({
			...counts,
			[infraNodeKind(group.node)]: counts[infraNodeKind(group.node)] + 1,
		}),
		{ base: 0, added: 0, dedicated: 0 } as Record<InfraNodeKind, number>,
	);
	const visibleGroups = filterNodeGroupsByKind(sortNodeGroupsByKind(groups), selectedKinds);

	return (
		<div
			data-testid="infra-page"
			style={{ padding: 32, height: "100%", overflowY: "auto", background: "var(--surface-1)" }}
		>
			<h2 style={pageTitleStyle}>ノードと環境の配置</h2>
			<p style={captionStyle({ margin: "6px 0 16px" })}>
				env ノードと、その上で動いている全組織の環境です。{REFRESH_INTERVAL_MS / 1000}{" "}
				秒ごとに読み直します。 CPU・メモリは request の合計 (DaemonSet の見積もりを含む) です。GPU
				専有ノードは環境の limit を出します。
				{placement !== null && ` 最終更新: ${formatTime(placement.observedAt)}`}
			</p>

			{error !== null && (
				<p
					data-testid="infra-error"
					style={{ margin: "0 0 16px", fontSize: "var(--text-ui)", color: "var(--danger)" }}
				>
					{error}
				</p>
			)}
			{actionNotice !== null && (
				<p
					data-testid="infra-action-notice"
					style={{ margin: "0 0 16px", fontSize: "var(--text-ui)", color: "var(--warning)" }}
				>
					{actionNotice}
				</p>
			)}
			{error === null && placement === null && (
				<p style={captionStyle({ margin: 0 })}>読み込み中…</p>
			)}

			<div
				data-testid="infra-summary"
				style={{
					display: "flex",
					flexWrap: "wrap",
					alignItems: "center",
					gap: 8,
					marginBottom: 24,
				}}
			>
				{placement !== null && (
					<>
						{INFRA_NODE_KINDS.map((kind) => (
							<KindFilterChip
								key={kind}
								label={`${INFRA_NODE_KIND_LABELS[kind]}ノード`}
								testId={`infra-filter-${kind}`}
								value={kindCounts[kind]}
								selected={selectedKinds.has(kind)}
								onClick={() => setSelectedKinds((current) => toggleKind(current, kind))}
							/>
						))}
						<SummaryChip label="環境" value={environmentCount} />
						<SummaryChip
							label="割り当て待ち"
							value={unplaced.length}
							tone={unplaced.length > 0 ? "warning" : undefined}
						/>
					</>
				)}
				{placement !== null && (
					<select
						className="ds-select"
						data-testid="infra-organization-filter"
						value={organizationId ?? ""}
						onChange={(e) => setSelectedOrganizationId(e.target.value || null)}
						style={{ width: 240, height: 32, marginLeft: "auto" }}
					>
						<option value="">すべての組織</option>
						{organizations.map((organization) => (
							<option key={organization.id} value={organization.id}>
								{organization.name} ({organization.environmentCount})
							</option>
						))}
					</select>
				)}
				<IconButton
					icon="rotate"
					title="ノードと環境の配置を更新"
					data-testid="infra-refresh"
					size={28}
					disabled={loading}
					onClick={load}
					style={{ marginLeft: placement === null ? "auto" : undefined }}
				/>
			</div>

			{placement !== null && (
				<>
					{unplaced.length > 0 && (
						<section data-testid="infra-unplaced" style={{ marginBottom: 24 }}>
							<h3 style={sectionHeadingStyle}>ノードの割り当て待ち</h3>
							<p style={captionStyle({ margin: "0 0 8px" })}>
								載せられるノードが無く、増設を待っている環境です。長く残る場合は増設の上限か AZ
								の空きを確認してください。
							</p>
							<EnvironmentTable
								environments={unplaced}
								now={now}
								showSchedulingMessage
								onAction={requestAction}
							/>
						</section>
					)}

					<section>
						<h3 style={sectionHeadingStyle}>ノード</h3>
						{groups.length === 0 && (
							<p style={captionStyle({ margin: 0 })}>
								{organizationId === null
									? "env ノードがありません。"
									: "この組織の環境が載っているノードはありません。"}
							</p>
						)}
						{groups.length > 0 && visibleGroups.length === 0 && (
							<p style={captionStyle({ margin: 0 })}>選んだ種類のノードはありません。</p>
						)}
						<div
							style={{
								display: "grid",
								gridTemplateColumns: "repeat(auto-fill, minmax(420px, 1fr))",
								gap: 16,
							}}
						>
							{visibleGroups.map((group) => (
								<NodeCard key={group.node.name} group={group} now={now} onAction={requestAction} />
							))}
						</div>
					</section>
				</>
			)}

			<ConfirmDialog
				open={pendingAction !== null}
				title={pendingAction ? `環境を${ENVIRONMENT_ACTION_LABELS[pendingAction.action]}` : ""}
				message={
					pendingAction ? confirmMessage(pendingAction.action, pendingAction.environment) : ""
				}
				confirmLabel={pendingAction ? ENVIRONMENT_ACTION_LABELS[pendingAction.action] : undefined}
				onConfirm={confirmPendingAction}
				onCancel={() => setPendingAction(null)}
			/>
		</div>
	);
}

function confirmMessage(action: EnvironmentAction, environment: InfraEnvironment): string {
	const owner = [environment.organizationName, environment.projectName].filter(Boolean).join(" / ");
	const target = `「${environment.environmentName ?? environment.environmentId}」${owner === "" ? "" : ` (${owner})`}`;
	return action === "stop"
		? `${target}を停止しますか？環境内のファイルは保持され、利用者が次に使うときに再び起動します。`
		: `${target}を再起動しますか？環境内のファイルは保持されますが、実行中のプロセスは終了します。`;
}

function NodeCard({
	group,
	now,
	onAction,
}: {
	group: InfraNodeGroup;
	now: Date;
	onAction: (action: EnvironmentAction, environment: InfraEnvironment) => void;
}) {
	const { node, environments } = group;
	const kind = infraNodeKind(node);
	const amiStatus = infraNodeAmiStatus(node);
	return (
		<div
			data-testid={`infra-node-${node.name}`}
			style={{
				padding: 16,
				borderRadius: "var(--radius-3)",
				background: "var(--surface-1)",
				boxShadow: "var(--shadow-card)",
				display: "flex",
				flexDirection: "column",
				gap: 12,
				minWidth: 0,
			}}
		>
			<div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
				<Pill label={INFRA_NODE_KIND_LABELS[kind]} />
				<span
					title={node.name}
					data-tooltip-if-truncated
					style={{
						flex: 1,
						minWidth: 0,
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						fontSize: "var(--text-ui)",
						fontWeight: 500,
						color: "var(--fg-1)",
					}}
				>
					{node.name}
				</span>
				{amiStatus && <Pill label={amiStatus.label} tone={amiStatus.tone} />}
				{!node.acceptsNewPods && <Pill label="新規配置なし" tone="warning" />}
				<NodeActionsMenu group={group} />
			</div>
			<div style={captionStyle({ display: "flex", flexWrap: "wrap", gap: "4px 12px" })}>
				<span>{node.instanceType}</span>
				<span>{node.zone}</span>
				<span>起動から {formatAge(node.createdAt, now)}</span>
				{node.replacementDeadline !== null && (
					<span style={{ color: "var(--warning)" }}>
						期限 {formatDateTime(node.replacementDeadline)}
					</span>
				)}
			</div>
			{kind !== "dedicated" && (
				<>
					<UsageBar
						label="CPU"
						requested={node.requestedMilliCpu}
						allocatable={node.allocatableMilliCpu}
						format={formatMilliCpu}
					/>
					<UsageBar
						label="メモリ"
						requested={node.requestedMemoryBytes}
						allocatable={node.allocatableMemoryBytes}
						format={formatBytes}
					/>
				</>
			)}
			{environments.length === 0 ? (
				<p style={captionStyle({ margin: 0 })}>環境は載っていません。</p>
			) : (
				<EnvironmentTable
					environments={environments}
					now={now}
					showLimit={kind === "dedicated"}
					onAction={onAction}
				/>
			)}
		</div>
	);
}

function UsageBar({
	label,
	requested,
	allocatable,
	format,
}: {
	label: string;
	requested: number;
	allocatable: number;
	format: (value: number) => string;
}) {
	const ratio = usageRatio(requested, allocatable);
	const color = ratio >= 0.9 ? "var(--danger)" : ratio >= 0.7 ? "var(--warning)" : "var(--accent)";
	return (
		<div>
			<div
				style={captionStyle({ display: "flex", justifyContent: "space-between", marginBottom: 4 })}
			>
				<span>{label}</span>
				<span>
					{format(requested)} / {format(allocatable)} ({Math.round(ratio * 100)}%)
				</span>
			</div>
			<div
				style={{
					height: 6,
					borderRadius: "var(--radius-full)",
					background: "var(--surface-2)",
					overflow: "hidden",
				}}
			>
				<div style={{ width: `${ratio * 100}%`, height: "100%", background: color }} />
			</div>
		</div>
	);
}

function EnvironmentTable({
	environments,
	now,
	showSchedulingMessage = false,
	showLimit = false,
	onAction,
}: {
	environments: InfraEnvironment[];
	now: Date;
	showSchedulingMessage?: boolean;
	/** 専有ノードは環境が 1 つだけなので、request ではなく使える上限 (limit) を出す。 */
	showLimit?: boolean;
	onAction: (action: EnvironmentAction, environment: InfraEnvironment) => void;
}) {
	return (
		<table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
			<thead>
				<tr style={captionStyle({ textAlign: "left" })}>
					<th style={{ ...cellStyle, fontWeight: 500 }}>環境</th>
					<th style={{ ...cellStyle, fontWeight: 500, width: 88 }}>状態</th>
					<th style={{ ...cellStyle, fontWeight: 500, width: 128 }}>
						{showLimit ? "limit" : "request"}
					</th>
					<th style={{ ...cellStyle, fontWeight: 500, width: 80 }}>経過</th>
					<th style={{ ...cellStyle, width: 28 }} />
				</tr>
			</thead>
			<tbody>
				{environments.map((environment) => {
					const status = infraEnvironmentStatus(environment);
					const owner = [environment.organizationName, environment.projectName]
						.filter(Boolean)
						.join(" / ");
					return (
						<tr
							key={environment.environmentId}
							data-testid={`infra-environment-${environment.environmentId}`}
						>
							<td style={cellStyle}>
								<div
									title={environment.environmentId}
									style={{
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap",
										fontSize: "var(--text-caption)",
										color: "var(--fg-1)",
									}}
								>
									{environment.environmentName ?? environment.environmentId}
								</div>
								{owner !== "" && (
									<div
										title={owner}
										data-tooltip-if-truncated
										style={captionStyle({
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
										})}
									>
										{owner}
									</div>
								)}
								{environment.scheduledRestartAt !== null && (
									<div style={captionStyle({ color: "var(--warning)", marginTop: 2 })}>
										再起動予定 {formatDateTime(environment.scheduledRestartAt)}
									</div>
								)}
								{showSchedulingMessage && environment.schedulingMessage !== null && (
									<div style={captionStyle({ color: "var(--warning)", marginTop: 2 })}>
										{environment.schedulingMessage}
									</div>
								)}
							</td>
							<td style={cellStyle}>
								<Pill label={status.label} tone={status.tone} />
							</td>
							<td style={captionStyle(cellStyle)}>
								{formatMilliCpu(showLimit ? environment.limitMilliCpu : environment.milliCpu)}
								<br />
								{formatBytes(showLimit ? environment.limitMemoryBytes : environment.memoryBytes)}
							</td>
							<td style={captionStyle(cellStyle)}>{formatAge(environment.createdAt, now)}</td>
							<td style={{ ...cellStyle, paddingRight: 0 }}>
								<EnvironmentActionsMenu environment={environment} onAction={onAction} />
							</td>
						</tr>
					);
				})}
			</tbody>
		</table>
	);
}

async function copyEnvironmentIds(environmentIds: string[], message: string): Promise<void> {
	await navigator.clipboard.writeText(environmentIds.join("\n"));
	useToastStore.getState().show({ type: "success", message });
}

/** ノードカードの右上の 3 点メニュー。 */
function NodeActionsMenu({ group }: { group: InfraNodeGroup }) {
	const buttonRef = useRef<HTMLButtonElement>(null);
	const [open, setOpen] = useState(false);
	return (
		<>
			<IconButton
				ref={buttonRef}
				icon="kebab"
				size={24}
				data-testid={`infra-node-actions-${group.node.name}`}
				onClick={() => setOpen((current) => !current)}
			/>
			{open && buttonRef.current && (
				<ContextMenu
					position={{ anchor: buttonRef.current, align: "right" }}
					onClose={() => setOpen(false)}
					minWidth={200}
					items={[
						{
							label: "すべての環境のIDをコピー",
							disabled: group.environments.length === 0,
							title: group.environments.length === 0 ? "環境が載っていません" : undefined,
							testId: "infra-node-copy-environment-ids",
							onSelect: () =>
								copyEnvironmentIds(
									group.environments.map((environment) => environment.environmentId),
									`${group.environments.length} 件の環境のIDをコピーしました。`,
								),
						},
					]}
				/>
			)}
		</>
	);
}

/** 環境の行の右端の 3 点メニュー。停止・再起動は確認ダイアログを経て行う。 */
function EnvironmentActionsMenu({
	environment,
	onAction,
}: {
	environment: InfraEnvironment;
	onAction: (action: EnvironmentAction, environment: InfraEnvironment) => void;
}) {
	const buttonRef = useRef<HTMLButtonElement>(null);
	const [open, setOpen] = useState(false);
	const disabledReason =
		environment.organizationId === null
			? "DB に環境が無いため操作できません"
			: environment.terminating
				? "終了中のため操作できません"
				: undefined;
	return (
		<>
			<IconButton
				ref={buttonRef}
				icon="kebab"
				size={24}
				data-testid={`infra-environment-actions-${environment.environmentId}`}
				onClick={() => setOpen((current) => !current)}
			/>
			{open && buttonRef.current && (
				<ContextMenu
					position={{ anchor: buttonRef.current, align: "right" }}
					onClose={() => setOpen(false)}
					minWidth={140}
					items={[
						{
							label: "環境のIDをコピー",
							testId: "infra-environment-copy-id",
							onSelect: () =>
								copyEnvironmentIds([environment.environmentId], "環境のIDをコピーしました。"),
						},
						{ separator: true },
						...(["stop", "restart"] as const).map((action) => ({
							label: ENVIRONMENT_ACTION_LABELS[action],
							destructive: action === "stop",
							disabled: disabledReason !== undefined,
							title: disabledReason,
							testId: `infra-environment-${action}`,
							onSelect: () => onAction(action, environment),
						})),
					]}
				/>
			)}
		</>
	);
}
